import { MakeLogicType, actions, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { router, urlToAction } from 'kea-router'

import { lemonToast } from '@posthog/lemon-ui'

import api from 'lib/api'
import { trackedActionToUrl } from 'lib/logic/scenes/trackedActionToUrl'
import { getDefaultInterval } from 'lib/utils/dateFilters'
import { eventUsageLogic } from 'lib/utils/eventUsageLogic'
import { objectsEqual } from 'lib/utils/objects'
import { dataWarehouseViewsLogic } from 'scenes/data-warehouse/saved_queries/dataWarehouseViewsLogic'
import { MaxContextInput, createMaxContextHelpers } from 'scenes/max/maxTypes'
import { teamLogic } from 'scenes/teamLogic'
import { urls } from 'scenes/urls'

// eslint-disable-next-line import/no-cycle
import { isRevenueAnalyticsPropertyFilters } from '~/queries/schema-guards'
import {
    DataTableNode,
    NodeKind,
    QuerySchema,
    RevenueAnalyticsBreakdown,
    RevenueAnalyticsPropertyFilters,
    RevenueAnalyticsTopCustomersGroupBy,
} from '~/queries/schema/schema-general'
import { Breadcrumb, DataWarehouseSavedQuery, InsightLogicProps, InsightShortId, SimpleIntervalType } from '~/types'

import { sourceManagementLogic } from 'products/data_warehouse/frontend/shared/logics/sourceManagementLogic'

import type { PaginatedResponse } from '../../../frontend/src/lib/api'
import type {
    CurrencyCode,
    RevenueAnalyticsEventItem,
    RevenueAnalyticsGoal,
} from '../../../frontend/src/queries/schema/schema-general'
import type { ExternalDataSource } from '../../../frontend/src/types'
import { revenueAnalyticsSettingsLogic } from './settings/revenueAnalyticsSettingsLogic'

export enum RevenueAnalyticsQuery {
    OVERVIEW,
    MRR,
    GROSS_REVENUE,
    METRICS,
    TOP_CUSTOMERS,
}

export const REVENUE_ANALYTICS_QUERY_TO_SHORT_ID: Record<RevenueAnalyticsQuery, InsightShortId> = {
    [RevenueAnalyticsQuery.OVERVIEW]: 'revenue-analytics-overview' as InsightShortId,
    [RevenueAnalyticsQuery.MRR]: 'revenue-analytics-mrr' as InsightShortId,
    [RevenueAnalyticsQuery.GROSS_REVENUE]: 'revenue-analytics-gross-revenue' as InsightShortId,
    [RevenueAnalyticsQuery.METRICS]: 'revenue-analytics-metrics' as InsightShortId,
    [RevenueAnalyticsQuery.TOP_CUSTOMERS]: 'revenue-analytics-top-customers' as InsightShortId,
}

export const REVENUE_ANALYTICS_QUERY_TO_NAME: Record<RevenueAnalyticsQuery, string> = {
    [RevenueAnalyticsQuery.OVERVIEW]: 'Revenue Analytics Overview',
    [RevenueAnalyticsQuery.MRR]: 'MRR',
    [RevenueAnalyticsQuery.GROSS_REVENUE]: 'Gross Revenue',
    [RevenueAnalyticsQuery.METRICS]: 'Revenue Metrics',
    [RevenueAnalyticsQuery.TOP_CUSTOMERS]: 'Top Customers',
}

export const REVENUE_ANALYTICS_DATA_COLLECTION_NODE_ID = 'revenue-analytics'

// Type needs to look like this to be able to apss this to
export const buildDashboardItemId = (queryType: RevenueAnalyticsQuery): InsightLogicProps['dashboardItemId'] => {
    return `new-AdHoc.revenue-analytics.${queryType}`
}

const getDefaultRevenueAnalyticsInterval = (dateFrom: string | null, dateTo: string | null): SimpleIntervalType => {
    const interval = getDefaultInterval(dateFrom, dateTo)
    return interval === 'day' ? 'day' : 'month'
}

const INITIAL_REVENUE_ANALYTICS_FILTER = [] as RevenueAnalyticsPropertyFilters
const INITIAL_DATE_FROM = 'yStart' as string | null
const INITIAL_DATE_TO = null as string | null
const INITIAL_INTERVAL: SimpleIntervalType = getDefaultRevenueAnalyticsInterval(INITIAL_DATE_FROM, INITIAL_DATE_TO)
const INITIAL_DATE_FILTER = {
    dateFrom: INITIAL_DATE_FROM,
    dateTo: INITIAL_DATE_TO,
    interval: INITIAL_INTERVAL,
}

const teamId = window.POSTHOG_APP_CONTEXT?.current_team?.id
const persistConfig = { persist: true, prefix: `${teamId}_v2__` }

const wrapWithDataTableNodeIfNeeded = (
    query: DataTableNode['source'],
    columns: string[],
    isNeeded: boolean
): QuerySchema => {
    if (!isNeeded) {
        return query
    }

    return {
        kind: NodeKind.DataTableNode,
        source: query,
        full: true,
        embedded: false,
        showActions: true,
        columns,
    }
}

const setQueryParams = (params: Record<string, string>): string => {
    const searchParams = { ...router.values.searchParams }
    const urlParams = new URLSearchParams(searchParams)
    Object.entries(params).forEach(([key, value]) => {
        urlParams.set(key, value)
    })

    return `${urls.revenueAnalytics()}${urlParams.toString() ? '?' + urlParams.toString() : ''}`
}

export type MRRMode = 'mrr' | 'arr'
export type DisplayMode = 'line' | 'area' | 'bar' | 'table'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface revenueAnalyticsLogicValues {
    dataWarehouseSavedQueries: DataWarehouseSavedQuery[] // dataWarehouseViewsLogic
    dataWarehouseSources: PaginatedResponse<ExternalDataSource> | null // revenueAnalyticsSettingsLogic
    events: RevenueAnalyticsEventItem[] // revenueAnalyticsSettingsLogic
    revenueGoals: RevenueAnalyticsGoal[] // revenueAnalyticsSettingsLogic
    baseCurrency: CurrencyCode // teamLogic
    allRevenueAnalyticsViews: DataWarehouseSavedQuery[]
    breadcrumbs: Breadcrumb[]
    breakdownProperties: RevenueAnalyticsBreakdown[]
    dateFilter: {
        dateFrom: string | null
        dateTo: string | null
        interval: SimpleIntervalType
    }
    disabledGrowthModeSelection: boolean
    disabledTopCustomersModeSelection: boolean
    hasRevenueAnalyticsViewsWithIssues: boolean
    hasRevenueEvents: boolean
    hasRevenueTables: boolean | null
    insightsDisplayMode: DisplayMode
    isRevenueAnalyticsEnabled: boolean
    maxContext: MaxContextInput[]
    mrrMode: MRRMode
    pausedRevenueViews: DataWarehouseSavedQuery[]
    queries: Record<RevenueAnalyticsQuery, QuerySchema>
    resumingSchedules: boolean
    resumingViewSchedule: Record<string, boolean>
    revenueAnalyticsFilter: RevenueAnalyticsPropertyFilters
    revenueEnabledDataWarehouseSources: ExternalDataSource[] | null
    revenueEnabledEvents: RevenueAnalyticsEventItem[]
    topCustomersDisplayMode: DisplayMode
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface revenueAnalyticsLogicActions {
    loadDataWarehouseSavedQueries: () => any // dataWarehouseViewsLogic
    reportRevenueAnalyticsBreakdownAdded: (
        breakdownProperty: string,
        breakdownType: string
    ) => {
        breakdownProperty: string
        breakdownType: string
    } // eventUsageLogic
    reportRevenueAnalyticsBreakdownRemoved: (
        breakdownProperty: string,
        breakdownType: string
    ) => {
        breakdownProperty: string
        breakdownType: string
    } // eventUsageLogic
    reportRevenueAnalyticsDateRangeChanged: (
        dateFrom: string | null,
        dateTo: string | null
    ) => {
        dateFrom: string | null
        dateTo: string | null
    } // eventUsageLogic
    reportRevenueAnalyticsFilterApplied: (filterCount: number) => {
        filterCount: number
    } // eventUsageLogic
    reportRevenueAnalyticsMRRModeChanged: (mrrMode: string) => {
        mrrMode: string
    } // eventUsageLogic
    loadSourcesSuccess: (
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              },
        payload?:
            | {
                  value: true
              }
            | undefined
    ) => {
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              }
        payload?: {
            value: true
        }
    } // sourceManagementLogic
    addBreakdown: (breakdown: RevenueAnalyticsBreakdown) => {
        breakdown: RevenueAnalyticsBreakdown
    }
    removeBreakdown: (breakdown: RevenueAnalyticsBreakdown) => {
        breakdown: RevenueAnalyticsBreakdown
    }
    resumeViewSchedule: (viewId: string) => {
        viewId: string
    }
    setBreakdownProperties: (breakdownProperties: RevenueAnalyticsBreakdown[]) => {
        breakdownProperties: RevenueAnalyticsBreakdown[]
    }
    setDates: (
        dateFrom: string | null,
        dateTo: string | null
    ) => {
        dateFrom: string | null
        dateTo: string | null
    }
    setInsightsDisplayMode: (displayMode: DisplayMode) => {
        displayMode: DisplayMode
    }
    setMRRMode: (mrrMode: MRRMode) => {
        mrrMode: MRRMode
    }
    setRevenueAnalyticsFilters: (revenueAnalyticsFilters: RevenueAnalyticsPropertyFilters) => {
        revenueAnalyticsFilters: RevenueAnalyticsPropertyFilters
    }
    setTopCustomersDisplayMode: (displayMode: DisplayMode) => {
        displayMode: DisplayMode
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface revenueAnalyticsLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        revenueEnabledEvents: (events: RevenueAnalyticsEventItem[]) => RevenueAnalyticsEventItem[]
        revenueEnabledDataWarehouseSources: (
            dataWarehouseSources: PaginatedResponse<ExternalDataSource> | null
        ) => ExternalDataSource[] | null
        disabledGrowthModeSelection: (dateFilter: {
            dateFrom: string | null
            dateTo: string | null
            interval: SimpleIntervalType
        }) => boolean
        disabledTopCustomersModeSelection: (dateFilter: {
            dateFrom: string | null
            dateTo: string | null
            interval: SimpleIntervalType
        }) => boolean
        hasRevenueEvents: (revenueEnabledEvents: RevenueAnalyticsEventItem[]) => boolean
        hasRevenueTables: (revenueEnabledDataWarehouseSources: ExternalDataSource[] | null) => boolean | null
        isRevenueAnalyticsEnabled: (hasRevenueTables: boolean | null, hasRevenueEvents: boolean) => boolean
        allRevenueAnalyticsViews: (dataWarehouseSavedQueries: DataWarehouseSavedQuery[]) => DataWarehouseSavedQuery[]
        pausedRevenueViews: (allRevenueAnalyticsViews: DataWarehouseSavedQuery[]) => DataWarehouseSavedQuery[]
        hasRevenueAnalyticsViewsWithIssues: (allRevenueAnalyticsViews: DataWarehouseSavedQuery[]) => boolean
        queries: (
            dateFilter: {
                dateFrom: string | null
                dateTo: string | null
                interval: SimpleIntervalType
            },
            revenueAnalyticsFilter: RevenueAnalyticsPropertyFilters,
            topCustomersDisplayMode: DisplayMode,
            breakdownProperties: RevenueAnalyticsBreakdown[]
        ) => Record<RevenueAnalyticsQuery, QuerySchema>
        maxContext: (queries: Record<RevenueAnalyticsQuery, QuerySchema>) => MaxContextInput[]
    }
}

export type revenueAnalyticsLogicType = MakeLogicType<
    revenueAnalyticsLogicValues,
    revenueAnalyticsLogicActions,
    Record<string, any>,
    revenueAnalyticsLogicMeta
>

export const revenueAnalyticsLogic = kea<revenueAnalyticsLogicType>([
    path(['products', 'revenueAnalytics', 'frontend', 'revenueAnalyticsLogic']),
    connect(() => ({
        values: [
            teamLogic,
            ['baseCurrency'],
            revenueAnalyticsSettingsLogic,
            ['events', 'dataWarehouseSources', 'goals as revenueGoals'],
            dataWarehouseViewsLogic,
            ['dataWarehouseSavedQueries'],
        ],
        actions: [
            sourceManagementLogic,
            ['loadSourcesSuccess'],
            dataWarehouseViewsLogic,
            ['loadDataWarehouseSavedQueries'],
            eventUsageLogic,
            [
                'reportRevenueAnalyticsMRRModeChanged',
                'reportRevenueAnalyticsDateRangeChanged',
                'reportRevenueAnalyticsFilterApplied',
                'reportRevenueAnalyticsBreakdownAdded',
                'reportRevenueAnalyticsBreakdownRemoved',
            ],
        ],
    })),
    actions({
        setDates: (dateFrom: string | null, dateTo: string | null) => ({ dateFrom, dateTo }),
        setRevenueAnalyticsFilters: (revenueAnalyticsFilters: RevenueAnalyticsPropertyFilters) => ({
            revenueAnalyticsFilters,
        }),
        setMRRMode: (mrrMode: MRRMode) => ({ mrrMode }),
        setInsightsDisplayMode: (displayMode: DisplayMode) => ({ displayMode }),
        setTopCustomersDisplayMode: (displayMode: DisplayMode) => ({ displayMode }),
        setBreakdownProperties: (breakdownProperties: RevenueAnalyticsBreakdown[]) => ({ breakdownProperties }),
        addBreakdown: (breakdown: RevenueAnalyticsBreakdown) => ({ breakdown }),
        removeBreakdown: (breakdown: RevenueAnalyticsBreakdown) => ({ breakdown }),
        resumeViewSchedule: (viewId: string) => ({ viewId }),
    }),
    reducers(() => ({
        dateFilter: [
            INITIAL_DATE_FILTER,
            persistConfig,
            {
                setDates: (_, { dateTo, dateFrom }) => ({
                    dateTo,
                    dateFrom,
                    interval: getDefaultRevenueAnalyticsInterval(dateFrom, dateTo),
                }),
            },
        ],
        revenueAnalyticsFilter: [
            INITIAL_REVENUE_ANALYTICS_FILTER,
            persistConfig,
            { setRevenueAnalyticsFilters: (_, { revenueAnalyticsFilters }) => revenueAnalyticsFilters },
        ],
        breakdownProperties: [
            [] as RevenueAnalyticsBreakdown[],
            persistConfig,
            {
                addBreakdown: (state, { breakdown }) => {
                    if (state.length >= 2) {
                        return state
                    }

                    if (state.some((b) => b.property === breakdown.property && b.type === breakdown.type)) {
                        return state
                    }

                    return [...state, breakdown]
                },
                removeBreakdown: (state, { breakdown }) => {
                    return state.filter((b) => b.property !== breakdown.property || b.type !== breakdown.type)
                },
                setBreakdownProperties: (_, { breakdownProperties }) => breakdownProperties,
            },
        ],
        mrrMode: [
            'mrr' as MRRMode,
            persistConfig,
            {
                setMRRMode: (_, { mrrMode }) => mrrMode,
            },
        ],
        insightsDisplayMode: [
            'line' as DisplayMode,
            persistConfig,
            {
                setInsightsDisplayMode: (_, { displayMode }) => displayMode,
            },
        ],
        topCustomersDisplayMode: [
            'line' as DisplayMode,
            persistConfig,
            {
                setTopCustomersDisplayMode: (_, { displayMode }) => displayMode,
                setDates: (state, { dateTo, dateFrom }) => {
                    const interval = getDefaultRevenueAnalyticsInterval(dateFrom, dateTo)
                    if (interval !== 'month') {
                        return 'table'
                    }

                    return state
                },
            },
        ],
        resumingSchedules: [
            false,
            {
                resumeViewSchedule: () => true,
                loadDataWarehouseSavedQueries: () => false,
            },
        ],
        resumingViewSchedule: [
            {} as Record<string, boolean>,
            {
                resumeViewSchedule: (state, { viewId }) => ({ ...state, [viewId]: true }),
                loadDataWarehouseSavedQueries: () => ({}),
            },
        ],
    })),
    selectors({
        breadcrumbs: [
            () => [],
            (): Breadcrumb[] => [
                {
                    key: 'RevenueAnalytics',
                    name: 'Revenue analytics',
                    path: urls.revenueAnalytics(),
                    iconType: 'revenue_analytics',
                },
            ],
        ],

        revenueEnabledEvents: [
            (s) => [s.events],
            (events: import('~/queries/schema').RevenueAnalyticsEventItem[]) => events,
        ],

        revenueEnabledDataWarehouseSources: [
            (s) => [s.dataWarehouseSources],
            (dataWarehouseSources: null | import('lib/api').PaginatedResponse<import('~/types').ExternalDataSource>) =>
                dataWarehouseSources === null
                    ? null
                    : dataWarehouseSources.results.filter((source) => source.revenue_analytics_config.enabled),
        ],

        disabledGrowthModeSelection: [
            (s) => [s.dateFilter],
            (dateFilter: { dateFrom: string | null; dateTo: string | null; interval: SimpleIntervalType }): boolean =>
                dateFilter.interval !== 'month',
        ],

        disabledTopCustomersModeSelection: [
            (s) => [s.dateFilter],
            (dateFilter: { dateFrom: string | null; dateTo: string | null; interval: SimpleIntervalType }): boolean =>
                dateFilter.interval !== 'month',
        ],

        hasRevenueEvents: [
            (s) => [s.revenueEnabledEvents],
            (events: import('~/queries/schema').RevenueAnalyticsEventItem[]): boolean => {
                return events.length > 0
            },
        ],

        hasRevenueTables: [
            (s) => [s.revenueEnabledDataWarehouseSources],
            (dataWarehouseSources: import('~/types').ExternalDataSource[] | null): boolean | null => {
                // Indicate loading state with `null` if we haven't loaded this yet
                if (dataWarehouseSources === null) {
                    return null
                }

                return Boolean(dataWarehouseSources.length)
            },
        ],

        isRevenueAnalyticsEnabled: [
            (s) => [s.hasRevenueTables, s.hasRevenueEvents],
            (hasRevenueTables: boolean | null, hasRevenueEvents: boolean): boolean => {
                return hasRevenueTables || hasRevenueEvents
            },
        ],

        allRevenueAnalyticsViews: [
            (s) => [s.dataWarehouseSavedQueries],
            (savedQueries: DataWarehouseSavedQuery[]): DataWarehouseSavedQuery[] => {
                if (!savedQueries) {
                    return []
                }
                return savedQueries.filter((query) => query.managed_viewset_kind === 'revenue_analytics')
            },
        ],

        pausedRevenueViews: [
            (s) => [s.allRevenueAnalyticsViews],
            (allRevenueAnalyticsViews: DataWarehouseSavedQuery[]): DataWarehouseSavedQuery[] => {
                if (!allRevenueAnalyticsViews) {
                    return []
                }
                const result = allRevenueAnalyticsViews.filter(
                    (query) =>
                        (!query.sync_frequency || query.sync_frequency === 'never') &&
                        (query.status === 'Failed' || query.latest_error)
                )
                return result
            },
        ],

        hasRevenueAnalyticsViewsWithIssues: [
            (s) => [s.allRevenueAnalyticsViews],
            (allRevenueAnalyticsViews: DataWarehouseSavedQuery[]): boolean => {
                return allRevenueAnalyticsViews.some((view) => view.status === 'Failed' || view.latest_error)
            },
        ],

        queries: [
            (s) => [s.dateFilter, s.revenueAnalyticsFilter, s.topCustomersDisplayMode, s.breakdownProperties],
            (
                dateFilter: {
                    dateFrom: string | null
                    dateTo: string | null
                    interval: SimpleIntervalType
                },
                revenueAnalyticsFilter: RevenueAnalyticsPropertyFilters,
                topCustomersDisplayMode: DisplayMode,
                breakdown: RevenueAnalyticsBreakdown[]
            ): Record<RevenueAnalyticsQuery, QuerySchema> => {
                const { dateFrom, dateTo, interval } = dateFilter
                const dateRange = { date_from: dateFrom, date_to: dateTo }

                const topCustomersGroupBy: RevenueAnalyticsTopCustomersGroupBy =
                    topCustomersDisplayMode === 'table' ? 'all' : 'month'

                return {
                    [RevenueAnalyticsQuery.OVERVIEW]: {
                        kind: NodeKind.RevenueAnalyticsOverviewQuery,
                        properties: revenueAnalyticsFilter,
                        dateRange,
                    },
                    [RevenueAnalyticsQuery.MRR]: {
                        kind: NodeKind.RevenueAnalyticsMRRQuery,
                        properties: revenueAnalyticsFilter,
                        breakdown,
                        interval,
                        dateRange,
                    },
                    [RevenueAnalyticsQuery.GROSS_REVENUE]: {
                        kind: NodeKind.RevenueAnalyticsGrossRevenueQuery,
                        properties: revenueAnalyticsFilter,
                        breakdown,
                        interval,
                        dateRange,
                    },
                    [RevenueAnalyticsQuery.METRICS]: {
                        kind: NodeKind.RevenueAnalyticsMetricsQuery,
                        properties: revenueAnalyticsFilter,
                        breakdown,
                        interval,
                        dateRange,
                    },
                    [RevenueAnalyticsQuery.TOP_CUSTOMERS]: wrapWithDataTableNodeIfNeeded(
                        {
                            kind: NodeKind.RevenueAnalyticsTopCustomersQuery,
                            properties: revenueAnalyticsFilter,
                            dateRange,
                            groupBy: topCustomersGroupBy,
                        },
                        ['name', 'customer_id', 'amount', 'month'],
                        topCustomersGroupBy === 'all'
                    ),
                }
            },
        ],

        maxContext: [
            (s) => [s.queries],
            (queries: Record<RevenueAnalyticsQuery, QuerySchema>): MaxContextInput[] => {
                return [
                    createMaxContextHelpers.insight(
                        {
                            id: RevenueAnalyticsQuery.MRR,
                            short_id: REVENUE_ANALYTICS_QUERY_TO_SHORT_ID[RevenueAnalyticsQuery.MRR],
                            name: REVENUE_ANALYTICS_QUERY_TO_NAME[RevenueAnalyticsQuery.MRR],
                            query: queries[RevenueAnalyticsQuery.MRR],
                        },
                        {
                            revenueAnalyticsQuery: RevenueAnalyticsQuery.MRR,
                        }
                    ),
                    createMaxContextHelpers.insight(
                        {
                            id: RevenueAnalyticsQuery.GROSS_REVENUE,
                            short_id: REVENUE_ANALYTICS_QUERY_TO_SHORT_ID[RevenueAnalyticsQuery.GROSS_REVENUE],
                            name: REVENUE_ANALYTICS_QUERY_TO_NAME[RevenueAnalyticsQuery.GROSS_REVENUE],
                            query: queries[RevenueAnalyticsQuery.GROSS_REVENUE],
                        },
                        {
                            revenueAnalyticsQuery: RevenueAnalyticsQuery.GROSS_REVENUE,
                        }
                    ),
                    createMaxContextHelpers.insight(
                        {
                            id: RevenueAnalyticsQuery.METRICS,
                            short_id: REVENUE_ANALYTICS_QUERY_TO_SHORT_ID[RevenueAnalyticsQuery.METRICS],
                            name: REVENUE_ANALYTICS_QUERY_TO_NAME[RevenueAnalyticsQuery.METRICS],
                            query: queries[RevenueAnalyticsQuery.METRICS],
                        },
                        {
                            revenueAnalyticsQuery: RevenueAnalyticsQuery.METRICS,
                        }
                    ),
                    createMaxContextHelpers.insight(
                        {
                            id: RevenueAnalyticsQuery.TOP_CUSTOMERS,
                            short_id: REVENUE_ANALYTICS_QUERY_TO_SHORT_ID[RevenueAnalyticsQuery.TOP_CUSTOMERS],
                            name: REVENUE_ANALYTICS_QUERY_TO_NAME[RevenueAnalyticsQuery.TOP_CUSTOMERS],
                            query: queries[RevenueAnalyticsQuery.TOP_CUSTOMERS],
                        },
                        {
                            revenueAnalyticsQuery: RevenueAnalyticsQuery.TOP_CUSTOMERS,
                        }
                    ),
                ]
            },
        ],
    }),
    listeners(({ actions }) => ({
        setMRRMode: ({ mrrMode }) => {
            actions.reportRevenueAnalyticsMRRModeChanged(mrrMode)
        },
        setDates: ({ dateFrom, dateTo }) => {
            actions.reportRevenueAnalyticsDateRangeChanged(dateFrom, dateTo)
        },
        setRevenueAnalyticsFilters: ({ revenueAnalyticsFilters }) => {
            actions.reportRevenueAnalyticsFilterApplied(revenueAnalyticsFilters.length)
        },
        addBreakdown: ({ breakdown }) => {
            actions.reportRevenueAnalyticsBreakdownAdded(breakdown.property, breakdown.type)
        },
        removeBreakdown: ({ breakdown }) => {
            actions.reportRevenueAnalyticsBreakdownRemoved(breakdown.property, breakdown.type)
        },
        resumeViewSchedule: async ({ viewId }) => {
            try {
                await api.dataWarehouseSavedQueries.resumeSchedules([viewId])
                lemonToast.success('Schedule resumed')
                actions.loadDataWarehouseSavedQueries()
            } catch {
                lemonToast.error('Failed to resume schedule')
                actions.loadDataWarehouseSavedQueries()
            }
        },
    })),
    trackedActionToUrl(() => ({
        setDates: ({ dateFrom, dateTo }): string =>
            setQueryParams({ date_from: dateFrom ?? '', date_to: dateTo ?? '' }),
        setRevenueAnalyticsFilters: ({ revenueAnalyticsFilters }): string =>
            setQueryParams({ filters: JSON.stringify(revenueAnalyticsFilters) }),
    })),
    urlToAction(({ actions, values }) => ({
        [urls.revenueAnalytics()]: (_, { filters, date_from, date_to }) => {
            if (
                (date_from && date_from !== values.dateFilter.dateFrom) ||
                (date_to && date_to !== values.dateFilter.dateTo)
            ) {
                actions.setDates(date_from, date_to)
            }

            const parsedFilters = isRevenueAnalyticsPropertyFilters(filters) ? filters : undefined
            if (parsedFilters && !objectsEqual(parsedFilters, values.revenueAnalyticsFilter)) {
                actions.setRevenueAnalyticsFilters(parsedFilters)
            }
        },
    })),
])
